import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SupportStyleTable from "../../../Components/Table/SupportStyleTable";
import { fetchUsers } from "../../../Redux/Slice/Homepage";
import "../../CustomerReq/InhouseRnd.css";
import {
  Building2,
  Phone,
  Mail,
  Eye,
  X,
  Users,
} from "lucide-react";

const ProfileModal = ({ isOpen, onClose, CustomerData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#133A69] to-[#0d284a] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <Building2 size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white leading-tight">Customer Profile</h3>
              <p className="text-xs text-slate-300">Detailed account information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5">
          {CustomerData ? (
            <div className="space-y-3.5">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Building2 size={16} className="text-[#133A69] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Company Name</span>
                  <span className="text-sm font-semibold text-slate-800 break-words">{CustomerData.CompanyName || "--"}</span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Mail size={16} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Email Address</span>
                  <span className="text-sm font-medium text-slate-700 break-words">{CustomerData.Email || "--"}</span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Phone size={16} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Mobile Number</span>
                  <span className="text-sm font-medium text-slate-700">{CustomerData.MobileNumber || "--"}</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Account Status</span>
                {CustomerData.Profile === "On" ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                    Inactive
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-slate-500 text-sm py-4">No profile data available.</p>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold bg-[#133A69] hover:bg-[#0d284a] text-white rounded-lg shadow-xs transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const HomeShowAllCustomer = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.Home);

  const [activeButton, setActiveButton] = useState("Active Customer");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleViewProfile = (profileData) => {
    setSelectedProfile(profileData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  const rows = useMemo(() => {
    return (
      data?.data?.map((el, index) => ({
        id: el.id || index + 1,
        CompanyName: el.WhaClientName,
        MobileNumber: el.ContactNo || "-",
        MainId: el._id,
        Profile: el.profile,
        Email: el.Email,
      })) || []
    );
  }, [data]);

  const totalCount = rows.length;
  const activeCount = rows.filter((row) => row.Profile === "On").length;
  const nonActiveCount = rows.filter((row) => row.Profile === "Off").length;

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (activeButton === "All Customer") return true;
      if (activeButton === "Active Customer") return row.Profile === "On";
      if (activeButton === "Non Active Customer") return row.Profile === "Off";
      return true;
    });
  }, [rows, activeButton]);

  // Reusable cell renderers
  const renderIdCell = (params) => (
    <span className="inline-flex items-center justify-center font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
      #{params.value}
    </span>
  );

  const renderCompanyCell = (params) => (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center text-[#133A69] shrink-0 font-bold text-[11px]">
        <Building2 size={13} />
      </div>
      <span className="font-semibold text-slate-800 truncate" title={params.value}>
        {params.value || "--"}
      </span>
    </div>
  );

  const renderMobileCell = (params) => (
    <div className="inline-flex items-center gap-1.5 text-slate-600 font-medium text-xs">
      <Phone size={12} className="text-slate-400 shrink-0" />
      <span>{params.value || "--"}</span>
    </div>
  );

  const renderStatusCell = (params) => {
    const isOnline = params.value === "On";
    return isOnline ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Active
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
        Inactive
      </span>
    );
  };

  const columns = [
    { field: "id", headerName: "ID", width: 65, minWidth: 60, renderCell: renderIdCell },
    { field: "CompanyName", headerName: "COMPANY NAME", minWidth: 260, renderCell: renderCompanyCell },
    { field: "MobileNumber", headerName: "MOBILE NUMBER", minWidth: 160, renderCell: renderMobileCell },
    { field: "Profile", headerName: "STATUS", width: 120, minWidth: 110, renderCell: renderStatusCell },
    {
      field: "profileLink",
      headerName: "ACTION",
      width: 125,
      minWidth: 125,
      renderCell: (params) => (
        <button
          type="button"
          onClick={() => handleViewProfile(params.row)}
          className="action-btn action-btn-show shadow-xs"
          title="View Customer Profile"
        >
          <Eye size={13} />
          <span>View Details</span>
        </button>
      ),
    },
  ];

  return (
    <div className="inhouse-card">
      {/* Card Header */}
      <div className="inhouse-card-header" style={{ borderLeft: "4px solid #133A69" }}>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-[#133A69]">
            Customers Directory
          </span>
          <span className="text-slate-500 text-xs font-medium">
            Directory of registered customer companies and contact details
          </span>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
          Total: {totalCount}
        </span>
      </div>

      {/* Card Body */}
      <div className="inhouse-card-body space-y-3">
        {/* Status Tabs Bar */}
        <div className="dashboard-tabs-bar">
          <button
            type="button"
            className={`tab-btn-status tab-btn-queue ${activeButton === "All Customer" ? "active" : ""}`}
            onClick={() => setActiveButton("All Customer")}
          >
            <span className="tab-status-dot"></span>
            <span>All Customer</span>
            <span className="tab-badge">{totalCount}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-complete ${activeButton === "Active Customer" ? "active" : ""}`}
            onClick={() => setActiveButton("Active Customer")}
          >
            <span className="tab-status-dot"></span>
            <span>Active Customer</span>
            <span className="tab-badge">{activeCount}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-holding ${activeButton === "Non Active Customer" ? "active" : ""}`}
            onClick={() => setActiveButton("Non Active Customer")}
          >
            <span className="tab-status-dot"></span>
            <span>Non Active Customer</span>
            <span className="tab-badge">{nonActiveCount}</span>
          </button>
        </div>

        {/* SupportStyleTable */}
        <SupportStyleTable
          tableId="home_all_customers"
          fileName={`Customers_${activeButton.replace(/\s+/g, "_")}`}
          columns={columns}
          rows={filteredRows}
          fullHeight={false}
          emptyComponent="No customers found in this category."
        />
      </div>

      {/* Modern Profile Modal */}
      <ProfileModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        CustomerData={selectedProfile}
      />
    </div>
  );
};

export default HomeShowAllCustomer;
